import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { ACCENT } from "../campanhas/CampanhaVisuals";
import { origemDaVisita, useCapturaDoWidget } from "./capturaLead";

// Página pública de contato (sem autenticação) — destino do CTA "Fale conosco"
// do site institucional (WordPress). Reaproveita o ACCENT azul da North já
// usado em CampanhaVisuals; o dourado é o mesmo hex oficial da paleta usado
// no site (#C4AE90), só que não existe como token no design system do CRM,
// por isso aparece em hex direto aqui (igual o resto desta página).
const DOURADO = "#C4AE90";
const AZUL_ESCURO = "#263751"; // ACCENT escurecido, usado no rodapé

// Widget inline (não mais iframe): o form é injetado direto no DOM da
// página, então deixa de ser cross-origin — dá pra configurar tracker no
// "Ações depois de enviar" do próprio formulário no Bitrix, porque aquele
// JS passa a rodar no contexto desta página em vez de isolado num frame.
const BITRIX_FORM_ID = "inline/11/wefsez";
const BITRIX_LOADER_URL =
  "https://cdn.bitrix24.com.br/b36586165/crm/form/loader_11.js";

const META_PIXEL_ID = "2332660860541801";

// Meta Pixel só nesta página (não em web/index.html): o app é uma SPA com
// rotas por hash, então um <script> no index.html carregaria em toda rota.
// Aqui o /contato é a única página pública que precisa do PageView pro Meta.
declare global {
  interface Window {
    fbq?: any;
  }
}

const useMetaPixel = (pixelId: string) => {
  useEffect(() => {
    if (window.fbq) {
      window.fbq("init", pixelId);
      window.fbq("track", "PageView");
      return;
    }

    const fbq: any = function (...args: unknown[]) {
      fbq.callMethod ? fbq.callMethod(...args) : fbq.queue.push(args);
    };
    window.fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);

    window.fbq("init", pixelId);
    window.fbq("track", "PageView");
  }, [pixelId]);
};

// Reproduz o snippet de embed "inline" que o Bitrix fornece (Colocar no
// site > código JS), montando os dois <script> à mão porque JSX/
// dangerouslySetInnerHTML não executa <script> injetado no DOM. O marcador
// (data-b24-form) precisa estar no container antes do loader carregar, pois
// é ele que o loader usa pra achar onde renderizar o formulário.
//
// O Bitrix não expõe nenhum evento de "form pronto" — antes de renderizar o
// form de verdade ele mostra um placeholder tracejado próprio, que fica
// visível por um instante e chama atenção. Pra cobrir isso com nosso próprio
// skeleton, observamos o container por mutação: quando o DOM para de mudar
// por 400ms, assumimos que o placeholder deles já virou o form final. Um
// timeout de segurança evita que o skeleton fique preso pra sempre se o
// script falhar ou o Bitrix nunca disparar mutação nenhuma.
const useBitrixForm = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  active: boolean,
  onReady: () => void,
) => {
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !active) return;

    const marker = document.createElement("script");
    marker.setAttribute("data-b24-form", BITRIX_FORM_ID);
    marker.setAttribute("data-skip-moving", "true");
    container.appendChild(marker);

    const loader = document.createElement("script");
    loader.async = true;
    loader.src = `${BITRIX_LOADER_URL}?${Math.floor(Date.now() / 180000)}`;
    container.appendChild(loader);

    let debounceTimer: ReturnType<typeof setTimeout>;
    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(onReady, 400);
    });
    observer.observe(container, { childList: true, subtree: true });
    const safetyTimeout = setTimeout(onReady, 8000);

    return () => {
      observer.disconnect();
      clearTimeout(debounceTimer);
      clearTimeout(safetyTimeout);
      container.innerHTML = "";
    };
  }, [containerRef, active, onReady]);
};

const textoRodape = (
  <p className="text-[#A8AEC0] text-xs">
    © {new Date().getFullYear()} North Business Team. Todos os direitos reservados.
    <span
      className="ml-3 font-semibold uppercase tracking-[2px]"
      style={{ color: DOURADO }}
    >
      #TimeNorth
    </span>
  </p>
);

/**
 * Dois layouts a partir de `lg`:
 *
 * - Desktop: a viewport é dividida ao meio. Toda a identidade da North (logo,
 *   chamada e rodapé) fica na coluna esquerda; a direita é exclusiva do
 *   formulário do Bitrix, sem card nem moldura — o iframe encosta nas bordas.
 * - Celular: vira uma pilha simples de header (logo + chamada), formulário e
 *   rodapé, e a página rola normalmente, porque o formulário não caberia junto
 *   do texto na altura de um celular.
 *
 * O rodapé é renderizado em dois pontos (um dentro da coluna esquerda, outro no
 * fim da pilha) porque as duas posições são mutuamente exclusivas por
 * breakpoint: se ele vivesse só dentro da coluna esquerda, no celular
 * apareceria empilhado *antes* do formulário.
 */
export const ContatoPage = () => {
  useMetaPixel(META_PIXEL_ID);

  // O Bitrix redireciona pra cá com ?obrigado=1 depois do envio (configurado
  // em "Ação de pós-envio" > Redirecionar, no painel do formulário) — é o
  // gatilho do evento de conversão, já que o form deixou de ser um iframe
  // isolado e o redirect passa pela nossa própria rota.
  const [searchParams] = useSearchParams();
  const enviado = searchParams.get("obrigado") === "1";

  // Cópia de cada envio do widget para o nosso banco (card "Assessoria" no
  // kanban) — o widget continua entregando ao Bitrix. Ver capturaLead.ts.
  const origem = useMemo(
    () => origemDaVisita(searchParams, new URLSearchParams(window.location.search), document.referrer),
    // Só na chegada: depois do envio a query vira ?obrigado=1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useCapturaDoWidget(origem);

  useEffect(() => {
    if (enviado && window.fbq) {
      window.fbq("track", "Lead");
    }
  }, [enviado]);

  const [formLoading, setFormLoading] = useState(true);
  const handleFormReady = useCallback(() => setFormLoading(false), []);
  const formContainerRef = useRef<HTMLDivElement>(null);
  useBitrixForm(formContainerRef, !enviado, handleFormReady);

  return (
    <div className="flex flex-col lg:h-screen lg:flex-row lg:overflow-hidden">
      <div
        className="flex flex-col lg:w-1/2"
        style={{ backgroundColor: ACCENT }}
      >
        <header className="shrink-0 px-8 pt-6 xl:px-16">
          <a href="https://northbusinessteam.com.br/" className="inline-block">
            <img
              src="/logos/logo_full.png"
              alt="North Business Team"
              className="h-9 w-auto"
            />
          </a>
        </header>

        <section className="flex-1 flex flex-col justify-center px-8 py-10 lg:py-12 xl:px-16">
          <p
            className="text-[11px] font-bold uppercase tracking-[6px] mb-4"
            style={{ color: DOURADO }}
          >
            — Fale com a North
          </p>
          <h1 className="text-white font-extrabold text-3xl xl:text-4xl leading-tight tracking-tight mb-4">
            Soluções para a sua <span style={{ color: DOURADO }}>operação</span>
          </h1>
          <p className="text-[#A8AEC0] leading-relaxed max-w-md">
            Preencha o formulário e um especialista da North entrará em contato
            para apresentar as soluções disponíveis.
          </p>
        </section>

        <footer
          className="hidden lg:block shrink-0 px-8 py-4 xl:px-16"
          style={{ backgroundColor: AZUL_ESCURO }}
        >
          {textoRodape}
        </footer>
      </div>

      {/* 720px é a altura que o formulário ocupava renderizado como iframe na
          largura de um celular — manteve-se o número pra não pular o layout;
          o widget inline se ajusta ao container do mesmo jeito. */}
      <div className="lg:w-1/2 h-[720px] lg:h-auto bg-white relative">
        {enviado ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-center px-8">
            <p className="text-lg font-semibold" style={{ color: AZUL_ESCURO }}>
              Mensagem recebida.
            </p>
            <p className="text-[#6b7280] mt-2 max-w-sm">
              Um especialista da North vai entrar em contato em breve.
            </p>
          </div>
        ) : (
          <>
            {/* Cobre o placeholder tracejado que o próprio widget do Bitrix
                mostra enquanto carrega — some quando o MutationObserver de
                useBitrixForm detecta que o form de verdade já renderizou. */}
            {formLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white">
                <div
                  className="h-8 w-8 rounded-full border-2 border-[#e5e7eb] animate-spin"
                  style={{ borderTopColor: ACCENT }}
                />
                <p className="text-sm text-[#6b7280]">Carregando formulário…</p>
              </div>
            )}
            <div ref={formContainerRef} className="w-full h-full" />
          </>
        )}
      </div>

      <footer
        className="lg:hidden px-8 py-4 text-center"
        style={{ backgroundColor: AZUL_ESCURO }}
      >
        {textoRodape}
      </footer>

      <noscript>
        <img
          height={1}
          width={1}
          style={{ display: "none" }}
          src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
          alt=""
        />
      </noscript>
    </div>
  );
};

ContatoPage.path = "/contato";
